import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest, ApiError } from '../../api/client';
import type { NodeId } from '../../lib/types';
import { PresenceStack } from '../presence/PresenceStack';
import { registerHandle } from '../../app/handles';
import { reportAdminScreen } from '../../app/router';

/**
 * Live verification: run the agent against the real system, then review before it counts.
 *
 * The rule this screen exists to enforce is that findings NEVER reach documentation
 * generation on their own. A person reads them and approves, and that approval is the only
 * thing that writes `Node.liveObservation`. Everything here is arranged around that: the
 * Approve button appears only when there is a finished run with a summary to approve, and
 * Discard is simply "close" — the findings stay unapproved and nothing is written.
 *
 * The poll was a bare `lvPoll` timeout handle cleared in closeLiveVerify() and nowhere else,
 * so a run still going when the screen went away another way kept polling. It is a ref with
 * an unmount cleanup here, like the generation console's.
 */

export interface LiveVerifyHandle {
  start: (nodeId: NodeId) => void;
  close: () => void;
}

interface Step {
  n: number;
  tool?: string;
  input?: { sql?: string } & Record<string, unknown>;
  result?: string;
  error?: string;
  screenshotFile?: string;
}

interface Run {
  runId?: string;
  nodeId: NodeId;
  targetName?: string;
  appPath?: string;
  state: string;
  steps: Step[];
  summary?: string;
  confidence?: string;
  procedure?: string[];
  keyFindings?: string[];
  openQuestions?: string[];
  error?: string;
}

/** running -> Stop only; done -> Approve/Discard; error -> neither. */
type Phase = 'running' | 'done' | 'error';

const errText = (e: unknown): string =>
  e instanceof ApiError
    ? (e.body as { error?: string } | undefined)?.error || `HTTP ${e.status}`
    : e instanceof Error
      ? e.message
      : 'Unknown error';

function StepView({ step, runId }: { step: Step; runId: string | null }) {
  const q = step.input?.sql ?? `${step.tool ?? ''} ${JSON.stringify(step.input ?? {})}`;
  const shot =
    runId && step.screenshotFile
      ? `/uploads/live-verify/${encodeURIComponent(runId)}/${encodeURIComponent(step.screenshotFile)}`
      : null;
  return (
    <div className="lv-step">
      <div className="lv-q">
        <span className="lv-n">{step.n}</span>
        {q}
      </div>
      {step.error ? (
        <div className="lv-r err">{step.error}</div>
      ) : (
        // Truncated: a step's raw result can be enormous, and this panel is a trail to
        // follow rather than a data dump.
        <div className="lv-r">{(step.result || '').slice(0, 600)}</div>
      )}
      {/* A web run's screenshots are documentation illustration as much as audit trail. */}
      {shot ? (
        <a href={shot} target="_blank" rel="noopener">
          <img className="lv-shot" src={shot} alt={`step ${step.n}`} />
        </a>
      ) : null}
    </div>
  );
}

function FindingList({ items, title, ordered }: { items?: string[]; title: string; ordered?: boolean }) {
  if (!items || !items.length) return null;
  const List = ordered ? 'ol' : 'ul';
  return (
    <div className="lv-card">
      <h4>{title}</h4>
      <List>
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </List>
    </div>
  );
}

export function LiveVerify({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  const [nodeId, setNodeId] = useState<NodeId | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [head, setHead] = useState('');
  const [status, setStatus] = useState('');
  const [phase, setPhase] = useState<Phase>('running');

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runIdRef = useRef<string | null>(null);
  runIdRef.current = runId;

  const stopPolling = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  // The URL's /admin/live-verify segment (plan 1.6): reported from an effect, idempotently,
  // because the review's internal close paths set `open` directly. (The URL is writable but
  // not followable — a run starts FROM a node, so a bare link opens the map.)
  useEffect(() => {
    reportAdminScreen('live-verify', open);
  }, [open]);

  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  const poll = useCallback(async () => {
    const id = runIdRef.current;
    if (!id) return;
    stopPolling();
    try {
      const r = await apiRequest<Run>(`/api/ai/live-verify/${id}`);
      setRun(r);
      const label = s.nodes[r.nodeId]?.label || r.nodeId;
      setHead(
        `${label} · ${r.targetName ?? ''}${r.appPath ? ` · ${r.appPath}` : ''} — ${r.state}` +
          (r.state === 'running' ? ` (${r.steps.length} queries so far)` : ''),
      );
      if (r.state === 'running') {
        setPhase('running');
        timer.current = setTimeout(() => void poll(), 2000);
        return;
      }
      setPhase(r.state === 'done' && r.summary ? 'done' : 'error');
    } catch (e) {
      setHead(`Lost contact with the run: ${errText(e)}`);
      setPhase('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling, s.nodes]);

  const api = useRef<LiveVerifyHandle>({ start: () => {}, close: () => {} });
  api.current.start = useCallback(
    (id: NodeId) => {
      setNodeId(id);
      setRunId(null);
      runIdRef.current = null;
      setRun(null);
      setStatus('');
      setHead('Starting…');
      setPhase('running');
      setOpen(true);

      void apiRequest<{ runId: string }>('/api/ai/live-verify', {
        method: 'POST',
        body: { nodeId: id },
      }).then(
        (d) => {
          setRunId(d.runId);
          runIdRef.current = d.runId;
          void poll();
        },
        (e) => {
          setHead(`Could not start: ${errText(e)}`);
          setPhase('error');
        },
      );
    },
    [poll],
  );
  api.current.close = useCallback(() => {
    stopPolling();
    setOpen(false);
  }, [stopPolling]);
  useEffect(() => {
    registerHandle('liveVerify', api.current);
  }, []);

  if (!open) return null;

  const close = () => {
    stopPolling();
    setOpen(false);
  };

  const cancel = async () => {
    if (!runId) return;
    setStatus('Stopping…');
    try {
      await apiRequest(`/api/ai/live-verify/${runId}/cancel`, { method: 'POST' });
    } catch {
      /* the poll shows the resulting state either way */
    }
  };

  const approve = async () => {
    if (!runId) return;
    setStatus('Saving…');
    try {
      await apiRequest(`/api/ai/live-verify/${runId}/approve`, { method: 'POST' });
      setStatus('Approved ✓');
      // Mirror the server locally so the popup says "Live-verified" without a reload. This is
      // the ONLY thing that writes an observation, which is why it is the only place that
      // touches the node.
      if (nodeId) callAction('applyLiveVerifyApproval', nodeId);
      setTimeout(close, 700);
    } catch (e) {
      setStatus(`Could not approve: ${errText(e)}`);
    }
  };

  const running = phase === 'running';
  const done = phase === 'done';

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-server" />
          </svg>
          Live verification
        </h2>
        <PresenceStack />
        {running ? (
          <button className="btn" id="lv-cancel-run" onClick={cancel}>
            <svg className="ico">
              <use href="#i-x" />
            </svg>
            <span>Stop</span>
          </button>
        ) : null}
        <button className="btn" id="lv-close" onClick={close}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Close</span>
        </button>
      </div>

      <div className="perm-wrap">
        <div className="perm-inner">
          <div id="lv-head" className="perm-section-lead">
            {head}
          </div>

          <div id="lv-findings">
            {run && run.state !== 'running' ? (
              run.state === 'done' && run.summary ? (
                <>
                  <div className="lv-card">
                    <h4>
                      Summary <span className="lv-conf">confidence: {run.confidence || '?'}</span>
                    </h4>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{run.summary}</p>
                  </div>
                  {/* The reproducible journey leads: it is the part a reader has to repeat. */}
                  <FindingList items={run.procedure} title="Steps to reproduce" ordered />
                  <FindingList items={run.keyFindings} title="Key findings" />
                  <FindingList items={run.openQuestions} title="Not verified" />
                </>
              ) : (
                <div className="lv-card">
                  <h4>{run.state}</h4>
                  <p>{run.error || 'The run produced no findings.'}</p>
                </div>
              )
            ) : null}
          </div>

          <label style={{ display: 'block', fontWeight: 600, margin: '18px 0 8px' }}>
            What the agent did
          </label>
          <div className="card">
            <div id="lv-steps" className="lv-steps">
              {!run || !run.steps.length ? (
                <div className="lv-step">
                  <span className="lv-r">{runId ? 'Thinking…' : 'Starting…'}</span>
                </div>
              ) : (
                run.steps.map((step) => <StepView key={step.n} step={step} runId={runId} />)
              )}
            </div>
          </div>
        </div>
      </div>

      <div id="aidoc-foot">
        {/* Discard is just "close": the findings stay unapproved, and nothing is written. */}
        {done ? (
          <button className="pbtn" id="lv-discard" onClick={close}>
            Discard
          </button>
        ) : null}
        {done ? (
          <button className="pbtn primary" id="lv-approve" onClick={approve}>
            Approve &amp; use as context
          </button>
        ) : null}
        <span id="lv-status">{status}</span>
      </div>
    </>
  );
}
